import { Link } from 'gatsby';
import React, { useEffect, useState } from 'react';
import {
  ArrowRight,
  Cpu,
  ExternalLink,
  Eye,
  MessageCircle,
  Terminal,
  Zap
} from 'lucide-react';
import styled from '../../styled';
import { COLORS } from '../../themes/color';
import { Headline } from '../Headline';
import { Kicker } from '../Kicker';
import { Reveal, usePrefersReducedMotion } from '../motion';

const INK = '#0e1c2b';
const CARD_BG = '#fff';
const CARD_BORDER = '#e2e8f0';
const TEXT_DIM = '#64748b';
const TEXT_BRIGHT = INK;

const SectionWrapper = styled('section')`
  position: relative;
  background: linear-gradient(180deg, #fff 0%, #f4f8fc 10%, #f4f8fc 90%, #fff 100%);
  background-color: #f4f8fc;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(
      rgba(14, 28, 43, 0.06) 1px,
      transparent 1px
    );
    background-size: 24px 24px;
    mask-image: radial-gradient(
      ellipse 75% 65% at 50% 40%,
      black 30%,
      transparent 100%
    );
    pointer-events: none;
  }
`;

const Inner = styled('div')`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 88px 24px 80px;

  @media (max-width: 960px) {
    padding: 64px 24px 56px;
  }
`;

const SectionHeader = styled('div')`
  text-align: center;
  margin-bottom: 56px;

  @media (max-width: 960px) {
    margin-bottom: 40px;
  }
`;

const SectionDescription = styled('p')`
  font-size: 18px;
  line-height: 1.6;
  color: ${TEXT_DIM};
  margin-top: 16px;
  max-width: 720px;
  margin-left: auto;
  margin-right: auto;
`;

/* ------------------------------ Bento ----------------------------- */

const BentoGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }

  /* Stretch reveal wrappers so tiles in a row share heights */
  > * {
    height: 100%;
    min-width: 0;
  }
`;

const Tile = styled('div')`
  position: relative;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 26px 24px;
  background: ${CARD_BG};
  border: 1px solid ${CARD_BORDER};
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(14, 28, 43, 0.04);
  transition: border-color 0.25s ease, box-shadow 0.25s ease,
    transform 0.25s ease;

  &:hover {
    border-color: ${COLORS.PRIMARY};
    box-shadow: 0 8px 30px rgba(24, 144, 255, 0.12);
    transform: translateY(-2px);
  }
`;

const HeroCell = styled(Reveal)`
  grid-column: span 2;

  @media (max-width: 960px) {
    grid-column: auto;
  }
`;

const HeroTile = styled(Tile)`
  padding: 26px 28px;

  /* Light streak pulsing along the top edge */
  &::after {
    @keyframes topShimmer {
      0%,
      100% {
        opacity: 0.25;
      }
      50% {
        opacity: 0.9;
      }
    }

    content: '';
    position: absolute;
    top: -1px;
    left: 12%;
    right: 12%;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(24, 144, 255, 0.55),
      transparent
    );
    animation: topShimmer 3.6s ease-in-out infinite;
    pointer-events: none;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
      opacity: 0.5;
    }
  }
`;

const TileTitleRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconWrapper = styled('div')`
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #e6f7ff;
  border: 1px solid #bae7ff;
  color: ${COLORS.PRIMARY};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TileTitle = styled('h3')`
  font-size: 17px;
  font-weight: 700;
  color: ${TEXT_BRIGHT};
  margin: 0;
`;

const TileDescription = styled('p')`
  font-size: 14px;
  line-height: 1.6;
  color: ${TEXT_DIM};
  margin: 0;
  flex: 1;
`;

const MicroCaption = styled('div')`
  font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  color: #94a3b8;
`;

/* --------------------------- Agent demo --------------------------- */

const DemoWindow = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-top: 4px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
  overflow: hidden;
  box-shadow: 0 4px 18px rgba(14, 28, 43, 0.05);
`;

const DemoHeader = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 14px;
  border-bottom: 1px solid #eef2f7;
  background: #fff;
  font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  color: #94a3b8;
`;

const LiveDot = styled('span')`
  @keyframes livePulse {
    0%,
    100% {
      box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.5);
    }
    50% {
      box-shadow: 0 0 0 4px rgba(82, 196, 26, 0);
    }
  }

  display: inline-flex;
  align-items: center;
  gap: 6px;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #52c41a;
    animation: livePulse 2.4s ease-in-out infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }
`;

const DemoBody = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px;
  min-height: 264px;

  @media (max-width: 600px) {
    min-height: 300px;
  }
`;

const UserBubble = styled('div')`
  align-self: flex-end;
  max-width: 85%;
  padding: 9px 14px;
  border-radius: 12px 12px 3px 12px;
  background: #eef2f7;
  border: 1px solid #e2e8f0;
  color: ${INK};
  font-size: 13.5px;
  line-height: 1.5;
`;

const ThinkingRow = styled('div')<{ $visible: boolean }>`
  @keyframes thinkingDot {
    0%,
    80%,
    100% {
      opacity: 0.25;
    }
    40% {
      opacity: 1;
    }
  }

  display: ${(p) => (p.$visible ? 'flex' : 'none')};
  align-items: center;
  gap: 8px;
  font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
  font-size: 11px;
  color: #94a3b8;

  i {
    display: inline-flex;
    gap: 3px;
    font-style: normal;

    span {
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background: ${COLORS.PRIMARY};
      animation: thinkingDot 1.2s ease-in-out infinite;

      &:nth-of-type(2) {
        animation-delay: 0.15s;
      }
      &:nth-of-type(3) {
        animation-delay: 0.3s;
      }

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    }
  }
`;

const Staged = styled('div')<{ $visible: boolean }>`
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transform: translateY(${(p) => (p.$visible ? 0 : 8)}px);
  transition: opacity 0.45s ease, transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);
`;

const ThoughtLine = styled(Staged)`
  font-size: 12px;
  color: #94a3b8;

  &::before {
    content: '‣ ';
  }
`;

const InlineCode = styled('code')`
  font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
  font-size: 11.5px;
  color: ${COLORS.PRIMARY};
  background: #e6f7ff;
  border-radius: 4px;
  padding: 1px 5px;
  white-space: nowrap;
`;

const AnswerIntro = styled(Staged)`
  font-size: 13px;
  line-height: 1.55;
  font-weight: 600;
  color: ${TEXT_BRIGHT};
`;

const SectionLabel = styled('div')`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #94a3b8;
`;

const FindingsList = styled('ul')`
  margin: 0;
  padding: 0 0 0 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FindingItem = styled('li')<{ $visible: boolean }>`
  font-size: 12.5px;
  line-height: 1.55;
  color: ${TEXT_DIM};
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transform: translateY(${(p) => (p.$visible ? 0 : 8)}px);
  transition: opacity 0.45s ease, transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);

  &::marker {
    color: #94a3b8;
  }
`;

const RecommendationBox = styled(Staged)`
  border-left: 3px solid ${COLORS.PRIMARY};
  border-radius: 6px;
  background: #e6f7ff;
  padding: 10px 14px;
  font-size: 12.5px;
  line-height: 1.55;
  color: ${INK};

  strong {
    display: block;
    margin-bottom: 3px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${COLORS.PRIMARY};
  }
`;

const AnswerFooter = styled(Staged)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const SourcesRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SourcePill = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  font-weight: 600;
  color: ${COLORS.PRIMARY};
  background: #e6f7ff;
  border-radius: 100px;
  padding: 4px 12px;
`;

const ActionButton = styled('span')`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 100px;
  background: ${COLORS.PRIMARY};
  color: #fff;
  font-size: 11.5px;
  font-weight: 600;
  white-space: nowrap;
`;

const DEMO_QUESTION = 'Which of our top pages have out-of-stock products?';

/**
 * Answer phases, mirroring the real agent UI: 0 idle, 1 thinking,
 * 2 thought line + intro, 3 findings, 4 second finding, 5 recommendation,
 * 6 sources + follow-ups.
 */
const AgentDemo = () => {
  const reduced = usePrefersReducedMotion();
  const [phase, setPhase] = useState(reduced ? 5 : 0);

  useEffect(() => {
    if (reduced) {
      setPhase(5);
      return;
    }
    const timers = [
      window.setTimeout(() => setPhase(1), 650),
      window.setTimeout(() => setPhase(2), 2200),
      window.setTimeout(() => setPhase(3), 2700),
      window.setTimeout(() => setPhase(4), 3300),
      window.setTimeout(() => setPhase(5), 3900)
    ];
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [reduced]);

  return (
    <DemoWindow>
      <DemoHeader>
        <span>affilimate agent</span>
        <LiveDot>live data</LiveDot>
      </DemoHeader>
      <DemoBody>
        <UserBubble>{DEMO_QUESTION}</UserBubble>
        <ThinkingRow $visible={phase === 1} aria-hidden>
          <i>
            <span />
            <span />
            <span />
          </i>
          checking product catalog…
        </ThinkingRow>
        <ThoughtLine $visible={phase >= 2}>Thought for 4 seconds</ThoughtLine>
        <AnswerIntro $visible={phase >= 2}>
          3 of your top-traffic pages link to out-of-stock products.{' '}
          <InlineCode>/best-espresso-machines</InlineCode> has the most.
        </AnswerIntro>
        <Staged $visible={phase >= 3}>
          <SectionLabel>Findings</SectionLabel>
        </Staged>
        <FindingsList>
          <FindingItem $visible={phase >= 3}>
            3 out-of-stock machines account for 41K pageviews in the last 30
            days. Updating these links could recover lost revenue.
          </FindingItem>
        </FindingsList>
        <RecommendationBox $visible={phase >= 4}>
          <strong>Recommendation</strong>
          Swap the affected links. 9 in-stock replacements found in your
          catalog.
        </RecommendationBox>
        <AnswerFooter $visible={phase >= 5}>
          <SourcesRow>
            <SectionLabel>Sources</SectionLabel>
            <SourcePill>
              Content report <ExternalLink size={11} />
            </SourcePill>
            <SourcePill>
              Product catalog <ExternalLink size={11} />
            </SourcePill>
          </SourcesRow>
          <ActionButton>Launch link swap campaign</ActionButton>
        </AnswerFooter>
      </DemoBody>
    </DemoWindow>
  );
};

/* ---------------------------- MCP tile ---------------------------- */

const MCP_CLIENTS = ['Claude', 'ChatGPT', 'Codex', 'Cursor', 'Gemini'];

const McpChips = styled('div')`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
`;

const McpChip = styled('span')`
  font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
  font-size: 11.5px;
  font-weight: 600;
  color: ${INK};
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 100px;
  padding: 5px 12px;
`;

const McpFanSvg = styled('svg')`
  @keyframes mcpFlow {
    from {
      stroke-dashoffset: 0;
    }
    to {
      stroke-dashoffset: -9;
    }
  }

  display: block;
  width: 100%;
  max-width: 250px;
  height: 44px;
  margin: 2px auto 0;
  overflow: visible;

  path {
    fill: none;
    stroke: rgba(24, 144, 255, 0.35);
    stroke-width: 1.2;
    stroke-dasharray: 3 6;
    animation: mcpFlow 1.4s linear infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }
`;

const McpCore = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 0 auto;
  padding: 8px 16px;
  border: 1px solid #bae7ff;
  border-radius: 100px;
  background: #e6f7ff;
  box-shadow: 0 4px 16px rgba(24, 144, 255, 0.1);

  img {
    width: 22px;
    height: 22px;
    border-radius: 50%;
  }

  span {
    font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
    font-size: 11px;
    letter-spacing: 0.06em;
    color: ${INK};
  }
`;

const MCP_FAN_W = 250;
const MCP_FAN_H = 44;
const MCP_FAN_TARGETS = [15, 70, 125, 180, 235];

const McpConnections = () => (
  <>
    <McpChips>
      {MCP_CLIENTS.map((client) => (
        <McpChip key={client}>{client}</McpChip>
      ))}
    </McpChips>
    <McpFanSvg
      viewBox={`0 0 ${MCP_FAN_W} ${MCP_FAN_H}`}
      preserveAspectRatio="none"
      aria-hidden
    >
      {MCP_FAN_TARGETS.map((x) => (
        <path
          key={x}
          d={`M ${x} 0 C ${x} 24, ${MCP_FAN_W / 2} 20, ${
            MCP_FAN_W / 2
          } ${MCP_FAN_H}`}
        />
      ))}
    </McpFanSvg>
    <McpCore>
      <img src="/favicon.png" alt="Affilimate" />
      <span>your workspace</span>
    </McpCore>
  </>
);

/* --------------------------- Section body ------------------------- */

const smallTiles = [
  {
    icon: <Cpu size={19} />,
    title: 'AI-assisted campaigns',
    description:
      'Describe a sponsorship in chat and get a campaign draft scoped to relevant pages. You review and launch.',
    caption: 'nothing ships without your approval'
  },
  {
    icon: <Eye size={19} />,
    title: 'Agent analytics',
    description:
      'See how AI assistants read and cite the pages you monetize, right next to clicks and commissions.',
    caption: 'agent traffic · per page'
  },
  {
    icon: <Zap size={19} />,
    title: 'Yield Optimizer',
    description:
      'Cross-merchant opportunities surfaced from your catalog-linked inventory, ready for review.',
    caption: '+$14K/mo in swap opportunities'
  }
];

const ExploreRow = styled('div')`
  display: flex;
  justify-content: center;
  margin-top: 36px;
`;

const ExploreLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${COLORS.PRIMARY};
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

export const AiPlatformSection = () => {
  return (
    <SectionWrapper id="ai-platform">
      <Inner>
        <SectionHeader>
          <Reveal>
            <Kicker>Early access</Kicker>
            <Headline component="h2">
              Don&apos;t just chat. Act.
            </Headline>
            <SectionDescription>
              Affilimate&apos;s AI platform is built for the commerce workflows
              your team runs every day. Catch revenue leaks, execute campaigns,
              and optimize yield in Affilimate or through MCP in the tools they
              already use.
            </SectionDescription>
          </Reveal>
        </SectionHeader>

        <BentoGrid>
          <HeroCell>
            <HeroTile>
              <TileTitleRow>
                <IconWrapper>
                  <MessageCircle size={19} />
                </IconWrapper>
                <TileTitle>Affilimate Agent</TileTitle>
              </TileTitleRow>
              <TileDescription style={{ flex: 'none' }}>
                Ask questions about your live commerce data and get answers
                backed by the underlying reports.
              </TileDescription>
              <AgentDemo />
            </HeroTile>
          </HeroCell>

          <Reveal delay={120}>
            <Tile>
              <TileTitleRow>
                <IconWrapper>
                  <Terminal size={19} />
                </IconWrapper>
                <TileTitle>Affilimate MCP</TileTitle>
              </TileTitleRow>
              <TileDescription style={{ flex: 'none' }}>
                Query commissions, page performance, catalog inventory, and
                custom dimensions from the AI tools you already use.
              </TileDescription>
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: 12
                }}
              >
                <McpConnections />
              </div>
              <ExploreLink to="/mcp/" style={{ marginTop: 16 }}>
                Explore Affilimate MCP <ArrowRight size={16} />
              </ExploreLink>
            </Tile>
          </Reveal>

          {smallTiles.map((tile, index) => (
            <Reveal key={tile.title} delay={index * 100}>
              <Tile>
                <TileTitleRow>
                  <IconWrapper>{tile.icon}</IconWrapper>
                  <TileTitle>{tile.title}</TileTitle>
                </TileTitleRow>
                <TileDescription>{tile.description}</TileDescription>
                <MicroCaption>{tile.caption}</MicroCaption>
              </Tile>
            </Reveal>
          ))}
        </BentoGrid>

        <Reveal delay={80}>
          <ExploreRow>
            <ExploreLink to="/ai-platform/">
              Explore the AI platform <ArrowRight size={20} />
            </ExploreLink>
          </ExploreRow>
        </Reveal>

      </Inner>
    </SectionWrapper>
  );
};
